// Extend Window interface to include Meta Pixel properties
import { AnalyticsInitParams, AnalyticsIntegration } from "./types.analytics"
import { TrackingEvent } from "@ncs/web-legos"

declare global {
	interface Window {
		fbq?: {
			(...args: unknown[]): void
			callMethod?: (...args: unknown[]) => void
			queue: unknown[]
			push: Window["fbq"]
			loaded: boolean
			version: string
		}
		_fbq?: Window["fbq"]
	}
}

class MetaPixelClient implements AnalyticsIntegration {
	private pixelId: string | null = null
	private initialized = false
	private context: Window | null = null

	// Mapping of custom events to standard Meta Pixel events
	// This ensures we use fbq('track', ...) for standard events and fbq('trackCustom', ...) for custom events
	private static readonly EVENT_MAPPING: Record<string, string> =
		MetaPixelClient.generateEventMapping()

	/**
	 * Generate event mapping dynamically based on TrackingEvent enum
	 * This ensures that if TrackingEvent values change, the mapping will automatically update
	 * without needing to manually maintain hardcoded strings
	 */
	private static generateEventMapping(): Record<string, string> {
		const mapping: Record<string, string> = {
			// Standard events that map directly
			PageView: "PageView",
			ViewContent: "ViewContent",
			AddToCart: "AddToCart",
			InitiateCheckout: "InitiateCheckout",
			Purchase: "Purchase",
			Search: "Search",
			AddPaymentInfo: "AddPaymentInfo",
			Lead: "Lead",
			CompleteRegistration: "CompleteRegistration",
			Contact: "Contact",
			CustomizeProduct: "CustomizeProduct",
			Donate: "Donate",
			FindLocation: "FindLocation",
			Schedule: "Schedule",
			StartTrial: "StartTrial",
			SubmitApplication: "SubmitApplication",
			Subscribe: "Subscribe",
		}

		// Add custom event mappings using TrackingEvent enum values
		// This ensures the mapping stays in sync with the enum
		const customMappings: Array<[TrackingEvent, string]> = [
			// Add to Cart events
			[TrackingEvent.ADD_TO_CART, "AddToCart"],
			[TrackingEvent.ADD_ITEMS_TO_CART, "AddToCart"],
			[TrackingEvent.PDP_ADD_TO_CART, "AddToCart"],
			[TrackingEvent.RP_ADD_TO_CART, "AddToCart"],
			[TrackingEvent.RV_ADD_TO_CART, "AddToCart"],
			[TrackingEvent.SR_ADD_TO_CART, "AddToCart"],
			[TrackingEvent.FLOATING_CART, "AddToCart"],

			// Checkout events
			[TrackingEvent.PROCEED_TO_CHECKOUT, "InitiateCheckout"],
			[TrackingEvent.CHECKOUT_VIEW, "InitiateCheckout"],

			// Purchase events
			[TrackingEvent.PLACE_ORDER, "Purchase"],
			[TrackingEvent.PURCHASE_ORDER, "Purchase"],

			// Search events
			[TrackingEvent.SEARCH, "Search"],
			[TrackingEvent.SEARCH_VIEW, "Search"],
			[TrackingEvent.SEARCH_RESULTS_VIEW, "Search"],
			[TrackingEvent.SEARCH_CATALOG, "Search"],

			// View Content events
			[TrackingEvent.PDP_VIEW, "ViewContent"],
			[TrackingEvent.CART_VIEW, "ViewContent"],
			[TrackingEvent.HOME_VIEW, "ViewContent"],
			[TrackingEvent.WELCOME_VIEW, "ViewContent"],
			[TrackingEvent.QUICK_ORDER_VIEW, "ViewContent"],

			// Registration events
			[TrackingEvent.SIGNUP, "CompleteRegistration"],
			[TrackingEvent.SIGNUP_ATTEMP, "CompleteRegistration"],
			[TrackingEvent.SIGNUP_VIEW, "CompleteRegistration"],

			// Contact events
			[TrackingEvent.CONTACT_US, "Contact"],

			// Lead events
			[TrackingEvent.LOGIN_VIEW, "Lead"],
			[TrackingEvent.SIGNIN, "Lead"],
			[TrackingEvent.SIGNIN_ATTEMP, "Lead"],
		]

		// Add custom mappings to the main mapping object
		// This approach ensures type safety and automatic updates when TrackingEvent enum changes
		customMappings.forEach(([trackingEvent, metaPixelEvent]) => {
			mapping[trackingEvent] = metaPixelEvent
		})

		return mapping
	}

	// Standard Meta Pixel events that should use fbq('track', ...)
	private static readonly STANDARD_EVENTS = new Set([
		"PageView",
		"ViewContent",
		"AddToCart",
		"InitiateCheckout",
		"Purchase",
		"Search",
		"AddPaymentInfo",
		"Lead",
		"CompleteRegistration",
		"Contact",
		"CustomizeProduct",
		"Donate",
		"FindLocation",
		"Schedule",
		"StartTrial",
		"SubmitApplication",
		"Subscribe",
	])

	public initialize(params: AnalyticsInitParams): void {
		const pixelId = params.accountId || params.apiKey || null
		if (!pixelId) {
			console.error("Error: Meta Pixel ID is not defined. Please provide a valid pixel ID.")
			return
		}

		if (this.initialized) {
			console.warn("Meta Pixel is already initialized")
			return
		}

		this.pixelId = pixelId
		this.setupMetaPixel()
		this.initialized = true
	}

	public isInitialized(): boolean {
		return this.initialized
	}

	public trackEvent(eventName: string, properties?: Record<string, unknown>): void {
		if (!this.pixelId || !this.context) return

		// Check if we have a mapping for this event
		const mappedEvent = MetaPixelClient.EVENT_MAPPING[eventName]
		const finalEventName = mappedEvent || eventName

		// Check if it's a standard event
		if (MetaPixelClient.STANDARD_EVENTS.has(finalEventName)) {
			this.context.fbq?.("track", finalEventName, properties)
		} else {
			// Use trackCustom for non-standard events
			this.context.fbq?.("trackCustom", finalEventName, properties)
		}
	}

	private setupMetaPixel(): void {
		const context = this.getContext()

		if (!context.fbq) {
			context.fbq = function (...args: unknown[]) {
				if (context.fbq?.callMethod) {
					context.fbq.callMethod(...args)
				} else if (context.fbq?.queue) {
					context.fbq.queue.push(args)
				}
			} as Window["fbq"]

			if (!context._fbq) context._fbq = context.fbq
			if (context.fbq) {
				context.fbq.push = context.fbq
				context.fbq.loaded = true
				context.fbq.version = "2.0"
				context.fbq.queue = []
			}
		}

		this.addMetaPixelScript(() => {
			if (this.pixelId && context.fbq) {
				context.fbq("init", this.pixelId)
				context.fbq("track", "PageView")
			}
		})

		this.addNoscriptFallback()
	}

	private addMetaPixelScript(callback?: () => void): void {
		const script = document.createElement("script")
		script.async = true
		script.src = "https://connect.facebook.net/en_US/fbevents.js"

		if (callback) {
			script.onload = callback
		}

		script.onerror = (e) => {
			console.error("Error loading Meta Pixel script:", e)
		}

		const firstScript = document.getElementsByTagName("script")[0]
		if (firstScript && firstScript.parentNode) {
			firstScript.parentNode.insertBefore(script, firstScript)
		} else {
			document.body.appendChild(script)
		}
	}

	private addNoscriptFallback(): void {
		if (!this.pixelId) return

		const noscript = document.createElement("noscript")
		const img = document.createElement("img")
		img.height = 1
		img.width = 1
		img.style.display = "none"
		img.src = `https://www.facebook.com/tr?id=${this.pixelId}&ev=PageView&noscript=1`

		noscript.appendChild(img)
		document.body.appendChild(noscript)
	}

	private getContext(): Window {
		if (!this.context) this.context = window
		return this.context
	}
}

export default MetaPixelClient
